import React, { useEffect, useRef } from "react";
import cn from "classnames";
import DOMPurify from "dompurify";
import styles from "./ticket-page-chat.module.scss";
import userIconBlue from "@src/assets/svg/user-icon-blue.svg";
import chatBotProfile from "@src/assets/svg/chat-bot-profile.svg";
import thumbsDownOutlineIcon from "@src/assets/svg/thumbs-down-icon-outline-blue.svg";
import thumbsUpOutlineIcon from "@src/assets/svg/thumbs-up-icon-outline-blue.svg";
import thumbsDownRedIcon from "@src/assets/svg/thumbs-down-icon-solid-red.svg";
import thumbsUpGreenIcon from "@src/assets/svg/thumbs-up-icon-solid-green.svg";
import sendIcon from "@src/assets/svg/transfer-arrow-icon.svg";
import sendIconActive from "@src/assets/svg/transfer-arrow-icon-active.svg";
import SpinnerWithVariant from "@src/components/atoms/SpinnerWithVariant/spinner-with-variant";
import closeIcon from "@src/assets/svg/close-with-circle-blue-icon.svg";
import { IconButton } from "@src/components/atoms/buttons/icon-button/icon-button";
import { CustomTooltip } from "@src/components/atoms/custom-tooltip/custom-tooltip";
import {
  UploadAttachment,
  UploadAttachmentHandle,
} from "@src/components/atoms/upload-attachment/upload-attachment";
import {
  ImagePickerButton,
  ImagePickerPreviews,
  useImagePaste,
} from "@src/components/atoms/image-picker/image-picker";
import attachIcon from "@src/assets/svg/attach-icon.svg";
import Spinner from "@src/components/atoms/spinner/spinner";
import { useI18N } from "@src/language/language-context";
import { TicketCommentAttachment, TicketCommentItem } from "@src/types/support";
import { isImageFile } from "@src/cloud-connect/support/utils/is-image-file";
import { isSupportHandoverMessage } from "@src/cloud-connect/support/utils/is-support-handover-message";
import { useTicketPageHookChat } from "./use-ticket-page-chat-hook";
import InitialsAvatar from "@src/components/atoms/initials-avatar/initials-avatar";
import { useSelector } from "react-redux";
import { RootState } from "@src/reducers/store";
import { TimezoneDate } from "@src/components/atoms/timezone-date/timezone-date";
import { downloadSupportAttachment } from "@src/cloud-connect/support/utils/download-from-support-service";
import { AuthImage } from "@src/components/atoms/auth-image/auth-image";
import { supportUrlEnvVar } from "@src/cloud-connect/feature-toggles/feature-toggles";
import { detectHtmlInComment } from "@src/cloud-connect/support/utils/detect-html-in-comment";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { errorSnackbar } from "@src/cloud-connect/snackbars/entities/snackbar-launcher";

const cssClassName = "ticket-page-chat";

const normalizeHtmlContent = (content: string): string => {
  let normalized = content;

  normalized = normalized
    .replace(/\\u003c/gi, "<")
    .replace(/\\u003e/gi, ">")
    .replace(/\\u0026/gi, "&")
    .replace(/\\u0022/gi, '"')
    .replace(/\\u0027/gi, "'")
    .replace(/\\"/g, '"')
    .replace(/\\n/g, "\n");

  const trimmed = normalized.trim();
  if (
    (trimmed.startsWith('"') && trimmed.endsWith('"')) ||
    (trimmed.startsWith("'") && trimmed.endsWith("'"))
  ) {
    try {
      normalized = JSON.parse(trimmed);
    } catch {
      // keep original if parsing fails
    }
  }

  if (/%3c|%3e|%26/i.test(normalized)) {
    try {
      normalized = decodeURIComponent(normalized);
    } catch {
      // keep original if decoding fails
    }
  }

  for (let i = 0; i < 5; i += 1) {
    const textarea = document.createElement("textarea");
    textarea.innerHTML = normalized;
    const next = textarea.value;
    if (next === normalized) break;
    normalized = next;
  }

  return normalized;
};

interface TicketPageChatProps {
  ticketId: string;
  refreshTrigger: number;
}

interface TicketPageChatMessageProps {
  message: TicketCommentItem;
  currentUserId: string;
  isFeedbackSubmitting: boolean;
  onFeedback: (messageId: string, useful: boolean) => void;
}

const TicketPageChatMessage = ({
  message,
  currentUserId,
  isFeedbackSubmitting,
  onFeedback,
}: TicketPageChatMessageProps) => {
  const isCurrentUserSameAsChatUser = currentUserId === message.author.id;
  const role = isCurrentUserSameAsChatUser ? "current-users" : "other-users";

  const hasTimestamp = Boolean(message.created_at);

  const handleAttachmentDownload = async (
    attachment: TicketCommentAttachment,
  ) => {
    await downloadSupportAttachment(attachment);
  };

  // Images (jpg/jpeg/png/webp) are the ones uploaded through the image-picker
  // flow and sent to the RAG agent so they render as thumbnails
  const isInlineImage = (attachment: TicketCommentAttachment): boolean =>
    !!attachment?.content_type && isImageFile(attachment.file_name, attachment.content_type);
  const fileAttachments =
    message?.attachments?.filter((attachment) => !isInlineImage(attachment)) ??
    [];
  const imageAttachments = message?.attachments?.filter(isInlineImage) ?? [];

  const content = normalizeHtmlContent(message.content);
  const isHtml = detectHtmlInComment(content);
  const canGiveFeedback =
    message.is_ai_chatbot_comment === true &&
    Boolean(message.message_id?.trim()) &&
    Boolean(content.trim()) &&
    !isSupportHandoverMessage(content);

  const handleFeedback = (useful: boolean) => {
    const messageId = message.message_id;
    if (messageId?.trim()) onFeedback(messageId, useful);
  };

  return (
    <div
      className={cn(
        styles[`${cssClassName}__message`],
        styles[`${cssClassName}__message--${role}`],
      )}
    >
      <div
        className={cn(
          styles[`${cssClassName}__message-content`],
          styles[`${cssClassName}__message-content--${role}`],
          hasTimestamp &&
            styles[`${cssClassName}__message-content--with-timestamp`],
        )}
      >
        {/* <div
          className={styles[`${cssClassName}__message-body`]}
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(normalizeHtmlContent(message.content)),
          }}
        /> */}
        <div className={styles[`${cssClassName}__message-body`]}>
          {isHtml ? (
            <div
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(content),
              }}
            />
          ) : (
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw]}
            >
              {content}
            </ReactMarkdown>
          )}
        </div>
        {fileAttachments.map((attachment) => (
          <button
            key={attachment.id}
            type="button"
            className={styles[`${cssClassName}__attachment`]}
            onClick={() => void handleAttachmentDownload(attachment)}
          >
            <img src={attachIcon} alt="" aria-hidden="true" />
            <span>{attachment.file_name}</span>
          </button>
        ))}
        {imageAttachments.length > 0 && (
          <div className={styles[`${cssClassName}__message-images`]}>
            {imageAttachments.map((attachment) => (
              <button
                key={attachment.id}
                type="button"
                title={attachment.file_name}
                className={styles[`${cssClassName}__message-image-button`]}
                onClick={() => void handleAttachmentDownload(attachment)}
              >
                {attachment.thumbnail_base64 ? (
                  <img
                    className={styles[`${cssClassName}__message-image`]}
                    src={`data:image/jpeg;base64,${attachment.thumbnail_base64}`}
                    alt={attachment.file_name}
                    title={attachment.file_name}
                  />
                ) : (
                  <AuthImage
                    className={styles[`${cssClassName}__message-image`]}
                    src={attachment.content_url}
                    baseUrl={supportUrlEnvVar()}
                    alt={attachment.file_name}
                    title={attachment.file_name}
                  />
                )}
              </button>
            ))}
          </div>
        )}
        {(canGiveFeedback || hasTimestamp) && (
          <div className={styles[`${cssClassName}__message-footer`]}>
            {canGiveFeedback && (
              <div className={styles[`${cssClassName}__feedback-actions`]}>
                <button
                  type="button"
                  aria-label="Thumbs down"
                  aria-pressed={message.useful === false}
                  className={styles[`${cssClassName}__feedback-button`]}
                  disabled={isFeedbackSubmitting}
                  onClick={() => handleFeedback(false)}
                >
                  <img
                    src={
                      message.useful === false
                        ? thumbsDownRedIcon
                        : thumbsDownOutlineIcon
                    }
                    alt=""
                  />
                </button>
                <button
                  type="button"
                  aria-label="Thumbs up"
                  aria-pressed={message.useful === true}
                  className={styles[`${cssClassName}__feedback-button`]}
                  disabled={isFeedbackSubmitting}
                  onClick={() => handleFeedback(true)}
                >
                  <img
                    src={
                      message.useful === true
                        ? thumbsUpGreenIcon
                        : thumbsUpOutlineIcon
                    }
                    alt=""
                  />
                </button>
              </div>
            )}
            {hasTimestamp && (
              <p className={styles[`${cssClassName}__timestamp`]}>
                <TimezoneDate
                  date={new Date(message.created_at)}
                  showRelative
                />
              </p>
            )}
          </div>
        )}
      </div>
      <div className={styles[`${cssClassName}__message-avatar`]}>
        {message?.is_ai_chatbot_comment !== true && message?.author?.name ? (
          <InitialsAvatar name={message.author.name} />
        ) : (
          <img
            src={isCurrentUserSameAsChatUser ? userIconBlue : chatBotProfile}
            alt={message.author?.name}
          />
        )}
      </div>
    </div>
  );
};

export const TicketPageChat = ({
  ticketId,
  refreshTrigger,
}: TicketPageChatProps) => {
  const i18n = useI18N();
  const sseUnavailableTooltip = i18n(
    "modules.sse.reconnectingActionsUnavailableTooltip",
  );
  const inputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const attachmentUploadRef = useRef<UploadAttachmentHandle>(null);

  const {
    comments,
    input,
    isLoading,
    isSending,
    selectedAttachment,
    isUploadingAttachment,
    selectedImages,
    attachmentRemoveKey,
    isSendIconDisabled,
    isSSEUnavailable,
    pendingFeedbackMessageIds,
    submitMessageFeedback,
    handleSendWithAttachment,
    handleClearAttachment,
    handleAttachmentChange,
    handleImagesChange,
    handleRemoveImage,
    handleUploadingChange,
    setInput,
  } = useTicketPageHookChat({ ticketId, refreshTrigger });

  const currentUserId = useSelector(
    (state: RootState) => state.yourSlice.authContext.userId,
  );
  const imageErrorMessages = {
    tooMany: i18n("modules.supportChatModal.imageTooMany"),
    tooLarge: i18n("modules.supportChatModal.imageSizeTooLarge"),
    invalid: i18n("modules.supportChatModal.imageInvalidType"),
  };
  const handleOtherFilesPaste = React.useCallback(
    (files: File[]) => {
      const uploader = attachmentUploadRef.current;
      if (!uploader) return false;

      if (files.length !== 1) {
        errorSnackbar("Please paste one attachment at a time.");
        return true;
      }
      if (selectedAttachment || isUploadingAttachment || isSending) {
        errorSnackbar(
          "Remove the current attachment before pasting another file.",
        );
        return true;
      }

      void uploader.uploadFile(files[0]);
      return true;
    },
    [isSending, isUploadingAttachment, selectedAttachment],
  );
  const handleImagePaste = useImagePaste(
    selectedImages,
    handleImagesChange,
    imageErrorMessages,
    isSending,
    handleOtherFilesPaste,
  );

  useEffect(() => {
    if (!isLoading) {
      requestAnimationFrame(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      });
    }
  }, [comments.length, isLoading]);

  useEffect(() => {
    inputRef.current?.focus();
  }, [ticketId]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (isSendIconDisabled) return;
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSendWithAttachment();
    }
  };

  return (
    <div className={styles[cssClassName]}>
      <div className={styles[`${cssClassName}__chat-container`]}>
        {isLoading ? (
          <div className={styles[`${cssClassName}__loading`]}>
            <Spinner />
          </div>
        ) : (
          <div className={styles[`${cssClassName}__messages-wrapper`]}>
            {comments.map((comment) => (
              <TicketPageChatMessage
                key={comment.id}
                message={comment}
                currentUserId={currentUserId}
                isFeedbackSubmitting={pendingFeedbackMessageIds.has(
                  comment.message_id ?? "",
                )}
                onFeedback={submitMessageFeedback}
              />
            ))}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      <div className={styles[`${cssClassName}__input-container`]}>
        <div className={styles[`${cssClassName}__input-row`]}>
          <input
            ref={inputRef}
            className={styles[`${cssClassName}__input`]}
            value={input}
            onChange={(event) => setInput(event.target.value)}
            onKeyDown={handleKeyDown}
            onPaste={handleImagePaste}
            placeholder={i18n("modules.supportChat.inputMessage")}
          />
          <ImagePickerButton
            images={selectedImages}
            onAdd={handleImagesChange}
            disabled={isSending}
            pickerLabel={i18n("modules.supportChatModal.imagePickerLabel")}
            errorMessages={imageErrorMessages}
          />
          <UploadAttachment
            ref={attachmentUploadRef}
            onAttachmentChange={handleAttachmentChange}
            onUploadingChange={handleUploadingChange}
            disabled={isSending}
            hasAttachment={!!selectedAttachment}
            attachmentRemoveKey={attachmentRemoveKey}
          />
          {isSending ? (
            <div className={styles[`${cssClassName}__spinner-wrapper`]}>
              <SpinnerWithVariant variant="small" />
            </div>
          ) : (
            <CustomTooltip
              content={isSSEUnavailable ? sseUnavailableTooltip : ""}
              position="top"
            >
              <IconButton
                fun={handleSendWithAttachment}
                icon={isSendIconDisabled ? sendIcon : sendIconActive}
                disabled={isSendIconDisabled}
              />
            </CustomTooltip>
          )}
        </div>
        {isSSEUnavailable && (
          <span
            className={styles[`${cssClassName}__visually-hidden`]}
            role="status"
          >
            {sseUnavailableTooltip}
          </span>
        )}
        <ImagePickerPreviews
          images={selectedImages}
          onRemove={handleRemoveImage}
          removeLabel={i18n("modules.supportChatModal.imageRemoveAlt")}
          disabled={isSending}
        />
        {selectedAttachment && (
          <div className={styles[`${cssClassName}__attachment-pill`]}>
            <span className={styles[`${cssClassName}__attachment-pill-name`]}>
              {selectedAttachment.file.name}
            </span>
            <IconButton
              fun={handleClearAttachment}
              icon={closeIcon}
              disabled={isSending}
            />
          </div>
        )}
      </div>
    </div>
  );
};
